import $ from 'jquery'
import _ from 'lodash'
import MultiSelector from 'scoreboard/components/multi-selector'
import DateTimeControl from 'scoreboard/components/date-time-control'
import { to24hrTime } from 'scoreboard/components/date-time-util'
import { getTimeString } from 'scoreboard/components/date-time-util'
import * as d3 from 'd3-selection'

/**
 * A MultiSelector which uses a DateTimeControl to pick multiple times or date/times
 * to display those times or date/times in it's input.
 */
var DateTimeMultiSelector = MultiSelector.extend({

	className: MultiSelector.prototype.className + ' date-time-multi-selector',

	_setupControl() {
		this._setTimes(this.options.times)
		this.dateTimeControl = new DateTimeControl({
			// TODO: When we need to show a multi-selector with a date and time
			// picker in the select dropdown, we will need to style it's appearance
			// in that dropdown. We aren't using this as of yet so leaving this
			// as a todo for the sake of time.
			// hasCalendarControl: this.options.hasCalendarControl
			timeDisplayType: this.options.timeDisplayType
		}).render()
	},

	_getActiveIndex() {
		// Check if there's an active tag. If so we need to adjust that object
		var activeIdx = this.selectOptions.findIndex(d => d.active)

		if (activeIdx !== -1) {
			// Remove the current active date since they changed it
			this.selectOptions.splice(activeIdx, 1)
		} else {
			// Fresh date selected, we'll throw it at the end of the array
			activeIdx = this.selectOptions.length
		}

		return activeIdx
	},

	_renderSelectAndFocusInput() {
		// timeControl is rebuilt in renderSelect() so preserve which input
		// was in focus (which should be the 'next' input set by time-control.js:goToNext())
		// to restore it after renderSelect(). It's important this get called
		// before this.renderSelect(true) because we're capturing the activeElement
		// at the point the time text input is still selected.
		let selectedInput = $(document.activeElement)
		// pass 'true' into renderSelect which keeps the dropdown open.
		// This is needed so that the user can continue to type in time
		// values as they pass from input to input automatically
		this.renderSelect(true)
		_.invoke(selectedInput, 'focus')
		selectedInput.focus()
		this._triggerSelectEvent()
	},

	_setupListeners() {
		this.dateTimeControl.on('onSetDateTime', dt => {
			if (this._rejectDuplicates(dt.string)) {
				return
			}

			// Update our times
			let value
			if (this.options.hasCalendarControl) {
				value = dt.string
			} else {
				value = this.dateTimeControl.timeControl.getTime().string
			}

			this.selectOptions.splice(this._getActiveIndex(), 0, {
				selected: true,
				active: true,
				label: value,
				value: to24hrTime(value),
				time: dt.time,
				date: dt.date,
				dataset: {
					type: this.options.hasCalendarControl ? 'date-time' : 'time'
				}
			})

			// dt.isIncomplete comes from time-control.js:_triggerChangeIfValid() and is
			// an indicator as to whether the time control values have been updated
			// in a way which should prompt a refresh of the time display (re-rendering our select)
			if (dt.isIncomplete) {
				return
			}

			this.isEnteringTime = true
			this._renderSelectAndFocusInput()
			this.isEnteringTime = false
		})
	},

	render: function() {
		MultiSelector.prototype.render.apply(this, arguments)
		// Hide the textarea in the select because it doesn't serve any function and
		// prevents the time control inputs from focusing.
		this.$el.next('.select').find('textarea').addClass('is-not-displayed')
		d3.select(this.el.nextSibling).datum().on('change:open', () => {
			// Since we're keeping the select menu open and the option being added
			// selected and updated as we enter in the time, we need to make sure that
			// the option does not appear active once the select menu goes away. This
			// would happen automatically when the select loses focus as the menu is opened,
			// but we're keeping it active here as the time is filled out.
			if (!this.el.nextSibling.classList.contains('menu-below')
				&& !this.el.nextSibling.classList.contains('menu-above')) {
				// If the active element is this select's textarea, that means the
				// select menu is closing because the user just clicked to the right
				// inside the select to enter a new time. This is handled in multi-selector.js
				// already, so just do nothing here if that's happening.
				if (this.$el.next().find(document.activeElement).length > 0) {
					return
				}
				this.selectOptions.forEach(o => delete o.active)
				this.renderSelect()
			// If we're opening the select menu, place focus on the first time input
			// so the user doesn't have to make a second click to start entering/changing time.
			} else {
				this.dateTimeControl.focus()
			}
		})
	},

	_compareResults(oldVal, newVal) {
		return oldVal === newVal
	},

	/*
	 * Draws the resultset of the rich selection menu. We override the rich select resultset
	 * renderer to draw a rome calendar selector. Extracted here so it can be overriden by subclasses
	 */
	_resultset: function (selection) {
		var dateTimeControl = this.dateTimeControl
		var activeTag = this.activeTag()
		var li = selection.selectAll('li').data(d => [d]) // Rebind to the entire array

		li.enter()
			.append('li')
			.classed('time-input-container', true)
			.select(function () {
				this.appendChild(dateTimeControl.el)
				return this
			})

		li.exit()
			.remove()

		// If we're currently entering time values into the control, leave the
		// control alone as to not disrupt the user.
		if (this.isEnteringTime) {
			return
		}

		if (activeTag) {
			dateTimeControl.setTime(activeTag.time)
			dateTimeControl.setDate(activeTag.date)
		} else {
			dateTimeControl.clear()
		}
	},

	_setTimes(times) {
		this.selectOptions = (times || []).map(time => {
			return {
				selected: true,
				label: getTimeString(time, this.options.timeDisplayType),
				value: to24hrTime(time),
				time,
				dataset: {
					type: 'time'
				}
			}
		})
	},

	remove: function() {
		MultiSelector.prototype.remove.apply(this, arguments)
		_.invokeMap([this.dateTimeControl], 'remove')
	}

})

export default DateTimeMultiSelector

DateTimeMultiSelector.__filename__ = 'components/date-time-multi-selector.js'
